import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const ServicesWrapper = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--background-dark) 100%
  );
  color: white;
  padding: 120px 0 80px;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 20px;
  color: white;
  font-family: "Inter", sans-serif;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: #e8e8e8;
  max-width: 600px;
  margin: 0 auto;
`;

const ServicesSection = styled.section`
  padding: 80px 0;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin-bottom: 80px;
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 40px 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  }

  .icon {
    width: 80px;
    height: 80px;
    background: var(--secondary-color);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 30px;
    color: white;
    font-size: 2rem;
  }

  h3 {
    font-size: 1.5rem;
    color: var(--text-dark);
    margin-bottom: 20px;
    text-align: center;
  }

  p {
    color: var(--text-light);
    line-height: 1.7;
    margin-bottom: 25px;
    text-align: center;
  }

  .features {
    list-style: none;
    padding: 0;
    margin-bottom: 30px;

    li {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 10px;
      color: var(--text-light);

      .check {
        color: var(--secondary-color);
        font-weight: bold;
      }
    }
  }
`;

const ServiceButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
  padding: 12px 24px;
  background: var(--secondary-color);
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: #c9a660;
    transform: translateY(-2px);
  }
`;

const ProcessSection = styled.section`
  background: var(--background-light);
  padding: 80px 0;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;
  margin-top: 60px;
`;

const ProcessStep = styled.div`
  text-align: center;

  .step-number {
    width: 60px;
    height: 60px;
    background: var(--secondary-color);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 auto 20px;
  }

  h3 {
    font-size: 1.25rem;
    color: var(--text-dark);
    margin-bottom: 15px;
  }

  p {
    color: var(--text-light);
    line-height: 1.6;
  }
`;

const Services = () => {
  const services = [
    {
      icon: "fas fa-shipping-fast",
      title: "New Containers",
      description:
        "Brand-new, one-trip containers perfect for long-term use and premium applications.",
      features: [
        "Clean, rust-free, and ready to go",
        "Ideal for businesses and construction sites",
        "Available in multiple sizes: 10ft, 20ft, 40ft",
        "Wind and watertight guarantee",
      ],
      link: "/containers",
    },
    {
      icon: "fas fa-recycle",
      title: "Used Containers",
      description:
        "Affordable and dependable used containers, carefully inspected to ensure durability.",
      features: [
        "Wind and watertight certified",
        "Cost-effective storage solution",
        "Available in multiple grades and sizes",
        "Thoroughly inspected for quality",
      ],
      link: "/containers",
    },
    {
      icon: "fas fa-tools",
      title: "Custom Modifications",
      description:
        "Turn your container into exactly what you need with our modification services.",
      features: [
        "Side doors, roll-up doors, and access points",
        "Windows, vents, and insulation",
        "Paint, branding, and interior finishes",
        "Office and workshop conversions",
      ],
      link: "/contact",
    },
    {
      icon: "fas fa-building",
      title: "Mobile Offices",
      description:
        "Complete office solutions with climate control, power, and modern amenities.",
      features: [
        "Fully insulated and climate controlled",
        "Electrical systems installed",
        "Windows and doors included",
        "Ready for immediate use",
      ],
      link: "/contact",
    },
    {
      icon: "fas fa-truck",
      title: "Nationwide Delivery",
      description:
        "Reliable delivery service covering all 50 states with professional logistics.",
      features: [
        "Coverage across all US states",
        "Professional delivery team",
        "Flexible scheduling options",
        "Competitive shipping rates",
      ],
      link: "/contact",
    },
    {
      icon: "fas fa-warehouse",
      title: "Storage Solutions",
      description:
        "Comprehensive storage solutions for personal and business needs.",
      features: [
        "Secure storage options",
        "Climate-controlled units available",
        "Flexible rental terms",
        "On-site and off-site solutions",
      ],
      link: "/contact",
    },
  ];

  const processSteps = [
    {
      number: "1",
      title: "Consultation",
      description:
        "Contact us to discuss your specific container needs and requirements.",
    },
    {
      number: "2",
      title: "Selection",
      description: "Choose from our inventory or request custom modifications.",
    },
    {
      number: "3",
      title: "Quote",
      description:
        "Receive a detailed quote including delivery to your location.",
    },
    {
      number: "4",
      title: "Delivery",
      description:
        "Professional delivery and placement at your specified location.",
    },
  ];

  return (
    <ServicesWrapper>
      <HeroSection>
        <Container>
          <HeroTitle>Our Services</HeroTitle>
          <HeroSubtitle>
            From sales to delivery, we provide comprehensive container solutions
            for all your needs
          </HeroSubtitle>
        </Container>
      </HeroSection>

      <ServicesSection>
        <Container>
          <ServicesGrid>
            {services.map((service, index) => (
              <ServiceCard key={index}>
                <div className="icon">
                  <i className={service.icon}></i>
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <ul className="features">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>
                      <span className="check">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <ServiceButton to={service.link}>
                  Learn More
                  <i className="fas fa-chevron-right"></i>
                </ServiceButton>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </Container>
      </ServicesSection>

      <ProcessSection>
        <Container>
          <h2 className="text-center mb-2">Our Process</h2>
          <p
            className="text-center text-light mb-5"
            style={{
              fontSize: "1.1rem",
              maxWidth: "600px",
              margin: "0 auto 60px",
            }}
          >
            Simple steps to get your perfect container solution
          </p>

          <ProcessGrid>
            {processSteps.map((step, index) => (
              <ProcessStep key={index}>
                <div className="step-number">{step.number}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </ProcessStep>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>
    </ServicesWrapper>
  );
};

export default Services;
